import type { ReactNode } from 'react';

import { StepCount, type UploadFile } from '@procurify/ui'

import { type CustomField } from '@webapp/models'
import { type Enum } from '@webapp/utils'
import { ImporterLoadingStage } from './components/ImporterLoadingStage/ImporterLoadingStage'
import { ImporterPreviewStage } from './components/ImporterPreviewStage/ImporterPreviewStage'
import { ImporterSuccessStage } from './components/ImporterSuccessStage/ImporterSuccessStage'
import { ImporterUploadStage } from './components/ImporterUploadStage/ImporterUploadStage'
import { type IImporterModifiedRowCounts } from './LegacyImporter'
import { ImporterStepIndicatorStyled as StepIndicator } from './styles/ImporterStepIndicatorStyled'
import { ImporterStyled } from './styles/ImporterStyled'
import {
  type ImporterColumnMap,
  type ImporterFieldMatch,
  type ImporterFieldNameMap,
  type ImporterOptionalFields,
  type ImporterRequiredFields,
  type ImporterSampleData,
} from './types'

export const BASE_IMPORTER_STAGES = {
  UPLOAD: 1,
  UPLOADING: 2,
  PREVIEW: 3,
  COMPLETE: 5,
} as const
export type BaseImporterStage = Enum<typeof BASE_IMPORTER_STAGES>

export interface IBaseImporterProps<ImporterStageType> {
  title: string
  requiredFields: ImporterRequiredFields
  optionalFields?: ImporterOptionalFields
  customfields?: CustomField[]
  forceOptionalCustomfields?: boolean
  fieldNameMapper?: ImporterFieldNameMap
  fieldMatcher?: ImporterFieldMatch
  criteria?: JSX.Element | JSX.Element[]
  templateEndpoint?: string
  importEndpoint?: string
  stage: ImporterStageType | BaseImporterStage
  sampleData: ImporterSampleData
  modifiedRowCounts: IImporterModifiedRowCounts
  noFileUploaded: boolean
  showUploadAssets?: boolean
  customStages?: {
    [key: number]: ReactNode
  }
  numColumnsInCSV?: number
  isButtonLoading?: boolean

  onDismiss?: () => void
  onUploadError?: () => void
  onFileAdded?: () => void
  onCSVUpload?: (file: UploadFile) => void
  onCSVRemove?: () => void
  onSubmitCSVUpload?: () => void
  onAssetUpload?: (file: UploadFile) => void
  onAssetRemove?: () => void
  onPreviewConfirm?: ({
    mappedColumns,
    ignoreFirstRow,
  }: {
    mappedColumns: ImporterColumnMap
    ignoreFirstRow: boolean
  }) => void
  onPreviewCancel?: () => void
}

export const BaseImporter = <ImportStageType, _ = any>({
  title,
  requiredFields,
  optionalFields = [],
  customfields = [],
  forceOptionalCustomfields = false,
  fieldNameMapper,
  fieldMatcher,
  criteria,
  templateEndpoint,
  importEndpoint,
  onDismiss,
  showUploadAssets,
  stage,
  sampleData,
  modifiedRowCounts,
  noFileUploaded,
  customStages,
  numColumnsInCSV,
  isButtonLoading = false,

  onUploadError = () => undefined,
  onFileAdded,
  onCSVUpload,
  onCSVRemove,
  onAssetUpload = () => undefined,
  onAssetRemove = () => undefined,
  onSubmitCSVUpload = () => undefined,
  onPreviewConfirm = () => undefined,
  onPreviewCancel = () => undefined,
}: IBaseImporterProps<ImportStageType>) => {
  const formatCustomfields = () => {
    const newRequiredFields = [...requiredFields]
    const newOptionalFields = [...optionalFields]
    const newFieldNameMapper = { ...fieldNameMapper }
    const newFieldMatcher = fieldMatcher ? { ...fieldMatcher } : {}

    if (customfields?.length) {
      customfields.forEach((customfield) => {
        if (!customfield.is_active) return

        if (!customfield.is_required || forceOptionalCustomfields) {
          newOptionalFields.push(`CSV_CUSTOM_FIELD_${customfield.id}`)
        } else {
          newRequiredFields.push(`CSV_CUSTOM_FIELD_${customfield.id}`)
        }

        newFieldNameMapper[`CSV_CUSTOM_FIELD_${customfield.id}`] =
          customfield.name
        newFieldMatcher[`CSV_CUSTOM_FIELD_${customfield.id}`] = [
          customfield.name,
        ]
      })
    }

    return {
      formattedRequiredFields: newRequiredFields,
      formattedOptionalFields: newOptionalFields,
      formattedFieldNameMapper: newFieldNameMapper,
      formattedFieldMatcher: newFieldMatcher,
    }
  }

  const {
    formattedRequiredFields,
    formattedOptionalFields,
    formattedFieldNameMapper,
    formattedFieldMatcher,
  } = formatCustomfields()

  const renderCustomStages = () => {
    const customStageKeys = Object.keys(customStages)

    return customStageKeys.map((customStage) => {
      return (
        (stage as number).toString() === customStage &&
        customStages[customStage]
      )
    })
  }

  return (
    <ImporterStyled>
      <StepIndicator>
        <StepCount step={stage as number} showLabel={false} total={5} />
      </StepIndicator>

      {stage === BASE_IMPORTER_STAGES.UPLOAD && (
        <ImporterUploadStage
          title={title}
          criteria={criteria}
          importEndpoint={importEndpoint}
          templateEndpoint={templateEndpoint}
          requiredFields={formattedRequiredFields}
          fieldNameMapper={formattedFieldNameMapper}
          onFileAdded={onFileAdded}
          onCSVUpload={onCSVUpload}
          onCSVRemove={onCSVRemove}
          onAssetUpload={onAssetUpload}
          onAssetRemove={onAssetRemove}
          onSubmit={onSubmitCSVUpload}
          onUploadError={onUploadError}
          onDismiss={onDismiss}
          showUploadAssets={showUploadAssets}
          disableSubmitButton={noFileUploaded}
          numColumnsInCSV={numColumnsInCSV}
          isButtonLoading={isButtonLoading}
        />
      )}

      {stage === BASE_IMPORTER_STAGES.UPLOADING && <ImporterLoadingStage />}

      {stage === BASE_IMPORTER_STAGES.PREVIEW && (
        <ImporterPreviewStage
          requiredFields={formattedRequiredFields}
          optionalFields={formattedOptionalFields}
          fieldNameMapper={formattedFieldNameMapper}
          fieldMatcher={formattedFieldMatcher}
          sampleData={sampleData}
          onConfirm={onPreviewConfirm}
          onDismiss={onPreviewCancel}
          isButtonLoading={isButtonLoading}
        />
      )}

      {customStages && renderCustomStages()}

      {stage === BASE_IMPORTER_STAGES.COMPLETE && (
        <ImporterSuccessStage
          modifiedRowCounts={modifiedRowCounts}
          itemName={title}
          onDismiss={onDismiss}
        />
      )}
    </ImporterStyled>
  )
}
